// Assigning English Skill Builders: pick the diagnostic the gaps came from, tick the skills to
// remediate, and stage them onto the lesson.
//
// A mirror of EngDiagPrereqTabPanel in chrome and flow, because a tutor moves between the two
// pills of the same panel and anything that reads differently looks like a different feature.
//
// WHAT THE TUTOR IS TICKING IS DIFFERENT, THOUGH. The prerequisite panel ticks CLUSTERS, because
// one prerequisite test covers many and the clusters decide its scope. Here each ticked SKILL
// becomes its own activity on the lesson plan - a Skill Builder is a skill - so the checkbox has
// to sit on the skill. Clusters are only the sections those skills are grouped under, with a
// tick-the-section shortcut on each header.
//
// Nothing is written here. Staging puts a draft on the lesson page, which posts it when the tutor
// presses Add Activities, the same as every other kind of draft.

import React, { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import Select, { SingleValue } from "react-select"
import { Input } from "reactstrap"
import { BMD_COLOURS } from "../../bmd/constants/bmd.colours"
import { plainText } from "../../bmd/utils/formatters"
import {
  getEngDiagSkillBuilderCandidates,
  previewEngDiagSkillBuilderAssignment,
  type EngDiagSkillBuilderCandidate,
  type EngDiagSkillBuilderPreview,
  type EngDiagSkillBuilderSkill,
} from "../services/eng-diag.service"

/**
 * Skill Builders waiting to be written, held on the lesson page until the tutor saves.
 *
 * ONE DRAFT PER DIAGNOSTIC, carrying every ticked skill - not one draft per skill. The backend
 * creates one activity per skill from a single call, so one draft matches one request, and the
 * staged card can say "3 skill builders" instead of stacking three near-identical cards for what
 * the tutor did in one action.
 *
 * Shares the page's skill-builder draft list with maths, discriminated by `subject_id`, the same
 * way prerequisite drafts already do. A second parallel list is what caused the duplicate heading
 * and the dead Add Activities button on the prerequisite side.
 */
export interface EngDiagSkillBuilderStagedDraft {
  /** Dedupe key for the staged list, and what the remove button passes back. */
  draft_key: string
  /** Routes the submit to engDiag rather than bmd. */
  subject_id: number
  student_key: string
  /** The diagnostic the gaps came from. Sent as `source_session_key` at submit time. */
  source_session_key: string
  year_level_name: string
  /** The skills the tutor ticked. One activity each. */
  skill_ids: number[]
  skill_count: number
  question_count: number
  /** For the staged card, so it does not have to go back to the API to name itself. */
  skill_names: string[]
  manual_marking_count: number
  is_homework?: 0 | 1
}

type Props = {
  studentKey: string
  /** Already staged in this visit, so the same diagnostic is not queued twice. */
  stagedKeys?: string[]
  onStageDraft: (draft: EngDiagSkillBuilderStagedDraft) => void
}

// ---------------------------------------------------------------------------
// Design tokens, shared with the prerequisite panel so the two cannot drift
// ---------------------------------------------------------------------------

/** Matches ENGLISH_SUBJECT_ID on the lesson page; routes the staged draft to the engDiag API. */
const ENGLISH_SUBJECT_ID = 1

const INK = BMD_COLOURS.INK
const BODY = BMD_COLOURS.BODY
const MUTED = BMD_COLOURS.MUTED
const CARD_BORDER = BMD_COLOURS.CARD_BORDER
const SECTION_BG = BMD_COLOURS.SECTION_BG
const ACCENT = BMD_COLOURS.ACCENT
/** The "how it works" paragraph, in the same readable navy the prerequisite panel uses. */
const EXPLAIN = "#35538c"
const SKILL = "#3f6cb5"

const CARD: React.CSSProperties = {
  backgroundColor: "#fff",
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: 10,
  padding: 20,
}

const LABEL: React.CSSProperties = {
  color: INK,
  fontSize: 12.5,
  fontWeight: 700,
  marginBottom: 8,
  display: "block",
}

const HELPER: React.CSSProperties = { color: MUTED, fontSize: 11.5 }

const STAT_LABEL: React.CSSProperties = {
  color: MUTED,
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
}

/** The theme sizes a bare checkbox to ~34px, which dwarfs a 13px skill name. */
const CHECKBOX: React.CSSProperties = {
  width: 16,
  height: 16,
  marginTop: 0,
  accentColor: INK,
  verticalAlign: "middle",
  cursor: "pointer",
  flexShrink: 0,
}

const BADGE: React.CSSProperties = {
  backgroundColor: SECTION_BG,
  color: BODY,
  borderRadius: 20,
  padding: "2px 8px",
  fontSize: 10.5,
  whiteSpace: "nowrap",
}

type CandidateOption = {
  value: string
  label: string
  candidate: EngDiagSkillBuilderCandidate
}

/** "31 Aug 2026". Fixed rather than locale-default, which gives "8/31/2026". */
function formatDate(iso: string | null): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
}

function candidateLabel(c: EngDiagSkillBuilderCandidate): string {
  const year = c.level_name || "Diagnostic"
  const when = formatDate(c.completed_on || c.created_on)
  // "gaps", not "incorrect": a skipped question counts here too, and calling it incorrect would
  // misreport what the child did.
  const gaps = `${c.gap_answer_count} gap${c.gap_answer_count === 1 ? "" : "s"}`
  const done = c.skill_builder_count > 0
    ? ` · ${c.skill_builder_count} already built`
    : ""
  return `${year}${when ? ` · ${when}` : ""} · ${gaps}${done}`
}

/** One column of the stat strip. */
const Stat: React.FC<{
  label: string
  value: React.ReactNode
  unit: string
  note?: string
}> = ({ label, value, unit, note }) => (
  <div style={{ minWidth: 130 }}>
    <div style={STAT_LABEL}>{label}</div>
    <div className="d-flex align-items-baseline gap-2 mt-1">
      <span style={{ color: INK, fontSize: 24, fontWeight: 700, lineHeight: 1 }}>
        {value}
      </span>
      <span style={{ color: BODY, fontSize: 12, maxWidth: 78, lineHeight: 1.2 }}>
        {unit}
      </span>
    </div>
    {note ? (
      <div style={{ color: MUTED, fontSize: 11, marginTop: 6, maxWidth: 150 }}>
        {note}
      </div>
    ) : null}
  </div>
)

/**
 * Why this skill is being offered, in the tutor's terms.
 *
 * Only shown when it is not the ordinary case. A wrong answer on the assigned test needs no
 * explanation; a skipped question or a gap found underneath the test does, because the tutor did
 * not choose that skill and would otherwise wonder where it came from.
 */
function reasonFor(skill: EngDiagSkillBuilderSkill): string | null {
  if (skill.was_skipped) return "skipped"
  if (!skill.on_base_test) return "found by prerequisites"
  return null
}

export function EngDiagSkillBuilderTabPanel({
  studentKey,
  stagedKeys = [],
  onStageDraft,
}: Props) {
  const [candidates, setCandidates] = useState<EngDiagSkillBuilderCandidate[]>([])
  const [loadingCandidates, setLoadingCandidates] = useState(false)
  const [candidatesError, setCandidatesError] = useState<string | null>(null)
  const [selected, setSelected] = useState<SingleValue<CandidateOption>>(null)
  const [preview, setPreview] = useState<EngDiagSkillBuilderPreview | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  useEffect(() => {
    if (!studentKey) return undefined
    const controller = new AbortController()
    setLoadingCandidates(true)
    setCandidatesError(null)
    getEngDiagSkillBuilderCandidates({ student_key: studentKey }, controller.signal)
      .then(data => {
        if (controller.signal.aborted) return
        setCandidates(data.candidates || [])
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setCandidatesError("Could not load this student's diagnostics.")
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingCandidates(false)
      })
    return () => controller.abort()
  }, [studentKey])

  const selectedKey = selected?.candidate.pk_session_key || ""

  useEffect(() => {
    if (!selectedKey) {
      setPreview(null)
      setPreviewError(null)
      return undefined
    }
    const controller = new AbortController()
    setLoadingPreview(true)
    setPreviewError(null)
    previewEngDiagSkillBuilderAssignment(
      { source_session_key: selectedKey, student_key: studentKey },
      controller.signal,
    )
      .then(data => {
        if (!controller.signal.aborted) setPreview(data)
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setPreviewError("Could not work out what this would assign.")
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingPreview(false)
      })
    return () => controller.abort()
  }, [selectedKey, studentKey])

  const options: CandidateOption[] = useMemo(
    () =>
      candidates.map(c => ({
        value: c.pk_session_key,
        label: candidateLabel(c),
        candidate: c,
      })),
    [candidates],
  )

  // Which skills are ticked. Everything starts ticked: the tutor is far more likely to want all
  // of a child's gaps than a subset, and un-ticking is less work than hunting for the ones to add.
  const [tickedIds, setTickedIds] = useState<number[]>([])
  const [openIds, setOpenIds] = useState<number[]>([])
  useEffect(() => {
    const clusters = preview?.clusters || []
    setTickedIds((preview?.skills || []).map(s => s.skill_id))
    // The first section opens so the affordance is visible without having to be discovered.
    setOpenIds(clusters.length > 0 ? [sectionKey(clusters[0].cluster_id)] : [])
  }, [preview])

  const tickedSet = useMemo(() => new Set(tickedIds), [tickedIds])
  const openSet = useMemo(() => new Set(openIds), [openIds])

  const tickedSkills = useMemo(
    () => (preview?.skills || []).filter(s => tickedSet.has(s.skill_id)),
    [preview, tickedSet],
  )
  const selectedQuestionCount = tickedSkills.reduce(
    (n, s) => n + (s.question_count || 0),
    0,
  )
  const selectedManualCount = tickedSkills.reduce(
    (n, s) => n + (s.manual_marking_count || 0),
    0,
  )

  const nothingOwed = !!preview && (preview.skills || []).length === 0
  const alreadyStaged = selectedKey ? stagedKeys.includes(selectedKey) : false
  const noneTicked = !!preview && !nothingOwed && tickedIds.length === 0
  const allTicked =
    !!preview && tickedIds.length === (preview.skills || []).length
  const canAdd = !!preview && !nothingOwed && !alreadyStaged && !noneTicked

  const toggleSkill = (id: number) =>
    setTickedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    )

  const toggleOpen = (key: number) =>
    setOpenIds(prev =>
      prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key],
    )

  /** Tick or clear a whole section, from its header checkbox. */
  const toggleSection = (skills: EngDiagSkillBuilderSkill[]) => {
    const ids = skills.map(s => s.skill_id)
    const allOn = ids.every(id => tickedSet.has(id))
    setTickedIds(prev =>
      allOn
        ? prev.filter(id => !ids.includes(id))
        : [...new Set([...prev, ...ids])],
    )
  }

  const handleStage = () => {
    if (!selected || !preview || !canAdd) return
    onStageDraft({
      draft_key: `engsb-${selectedKey}`,
      subject_id: ENGLISH_SUBJECT_ID,
      student_key: studentKey,
      source_session_key: selectedKey,
      year_level_name: selected.candidate.level_name,
      skill_ids: tickedSkills.map(s => s.skill_id),
      skill_count: tickedSkills.length,
      question_count: selectedQuestionCount,
      skill_names: tickedSkills.map(s => plainText(s.skill_name || "") || ""),
      manual_marking_count: selectedManualCount,
    })
    // Names the panel it landed in. Sending the tutor to the wrong one would have them hunting
    // for something that did save.
    toast.success("Added to Skill Builders", { autoClose: 2000 })
    setSelected(null)
  }

  return (
    <div className="p-2">
      {/* ------------------------------------------------------------------ */}
      {/* Which diagnostic the gaps came from                                 */}
      {/* ------------------------------------------------------------------ */}
      <div style={CARD}>
        <label style={LABEL} htmlFor="eng-sb-source">
          Build from a diagnostic with gaps
        </label>
        <div style={{ maxWidth: 460 }}>
          <Select
            inputId="eng-sb-source"
            value={selected}
            onChange={setSelected}
            options={options}
            isLoading={loadingCandidates}
            isClearable
            placeholder={
              loadingCandidates
                ? "Loading diagnostics…"
                : options.length === 0
                  ? "No diagnostics with gaps"
                  : "Select a diagnostic"
            }
            classNamePrefix="select2-selection"
          />
        </div>
        <div style={{ ...HELPER, marginTop: 8 }}>
          A gap is a question answered incorrectly or skipped. Questions still waiting to be
          marked are not counted until you mark them.
        </div>
      </div>

      {candidatesError ? (
        <div className="text-danger font-size-13 mt-3">{candidatesError}</div>
      ) : null}

      {selected && loadingPreview ? (
        <div className="text-muted font-size-13 mt-3">Loading…</div>
      ) : null}

      {previewError ? (
        <div className="text-danger font-size-13 mt-3">{previewError}</div>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* What would be assigned                                              */}
      {/* ------------------------------------------------------------------ */}
      {selected && preview && !loadingPreview ? (
        <div style={{ ...CARD, marginTop: 16 }}>
          <div className="d-flex flex-wrap align-items-start gap-4">
            <Stat
              label="Assigns now"
              value={tickedSkills.length}
              unit={tickedSkills.length === 1 ? "skill builder" : "skill builders"}
              note="One activity per skill on the lesson plan."
            />
            <Stat
              label="Questions"
              value={selectedQuestionCount}
              unit="in total"
              note={
                selectedManualCount > 0
                  ? `${selectedManualCount} you will mark by hand.`
                  : "All marked automatically."
              }
            />
            <div
              style={{ width: 1, alignSelf: "stretch", backgroundColor: CARD_BORDER }}
              aria-hidden="true"
            ></div>
            <div style={{ flex: "1 1 320px", minWidth: 240 }}>
              <div style={STAT_LABEL}>How it works</div>
              <div
                style={{
                  color: EXPLAIN,
                  fontSize: 12.5,
                  lineHeight: 1.55,
                  marginTop: 6,
                }}
              >
                A Skill Builder gives the student every question written for that skill, answered
                in the app. Unlike the prerequisite test it does not grow as they work - what you
                see here is the whole thing.
              </div>
            </div>
          </div>

          {nothingOwed ? (
            <div
              className="d-flex align-items-start gap-3 mt-4"
              style={{
                backgroundColor: "#fdf6e7",
                border: "1px solid #f0e0bd",
                borderRadius: 8,
                padding: "14px 16px",
              }}
            >
              <i
                className="mdi mdi-alert-outline"
                style={{ color: "#b0862f", fontSize: 16, lineHeight: 1.4 }}
                aria-hidden="true"
              ></i>
              <div>
                <div style={{ color: INK, fontSize: 13, fontWeight: 700 }}>
                  Nothing to assign from this diagnostic
                </div>
                <div style={{ color: BODY, fontSize: 12.5, marginTop: 2 }}>
                  {preview.already_assigned_skill_ids.length > 0
                    ? "Every skill with a gap already has a Skill Builder."
                    : preview.failed_skill_count > 0
                      ? "The gaps here are ones the prerequisite test handles, not Skill Builders."
                      : "No gaps have been found in this diagnostic."}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="d-flex align-items-center flex-wrap gap-2 mt-4 mb-2">
                <span style={{ color: INK, fontSize: 13, fontWeight: 700 }}>
                  Which skills to remediate
                </span>
                <span style={BADGE}>
                  {tickedSkills.length} of {(preview.skills || []).length} ticked
                </span>
                <button
                  type="button"
                  className="btn btn-link p-0 ms-auto"
                  style={{ color: ACCENT, fontSize: 12, textDecoration: "none" }}
                  onClick={() =>
                    setTickedIds(
                      allTicked ? [] : (preview.skills || []).map(s => s.skill_id),
                    )
                  }
                >
                  {allTicked ? "Clear all" : "Select all"}
                </button>
              </div>

              {preview.clusters.map(cluster => {
                const key = sectionKey(cluster.cluster_id)
                const isOpen = openSet.has(key)
                const sectionTicked = cluster.skills.filter(s =>
                  tickedSet.has(s.skill_id),
                ).length
                return (
                  <div
                    key={key}
                    style={{
                      border: `1px solid ${CARD_BORDER}`,
                      borderRadius: 8,
                      marginBottom: 10,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      className="d-flex align-items-center gap-3"
                      style={{
                        backgroundColor: SECTION_BG,
                        padding: "12px 14px",
                        cursor: "pointer",
                      }}
                      onClick={() => toggleOpen(key)}
                    >
                      <Input
                        type="checkbox"
                        id={`eng-sb-section-${key}`}
                        checked={sectionTicked === cluster.skills.length}
                        // Part-ticked reads as neither on nor off, which is what it is.
                        innerRef={(el: HTMLInputElement | null) => {
                          if (el) {
                            el.indeterminate =
                              sectionTicked > 0 &&
                              sectionTicked < cluster.skills.length
                          }
                        }}
                        onChange={() => toggleSection(cluster.skills)}
                        // The header opens the section; the checkbox must only tick, or a tutor
                        // aiming for the box also collapses what they were reading.
                        onClick={e => e.stopPropagation()}
                        style={CHECKBOX}
                        className="m-0"
                      />
                      <span
                        style={{
                          color: INK,
                          fontSize: 13,
                          fontWeight: 700,
                          flexGrow: 1,
                          minWidth: 0,
                        }}
                      >
                        {plainText(cluster.display_name)}
                      </span>
                      <span style={{ color: MUTED, fontSize: 12 }}>
                        {cluster.skill_count} skill
                        {cluster.skill_count === 1 ? "" : "s"} ·{" "}
                        {cluster.question_count} question
                        {cluster.question_count === 1 ? "" : "s"}
                      </span>
                      <i
                        className={`mdi mdi-chevron-${isOpen ? "up" : "down"}`}
                        style={{ color: MUTED, fontSize: 18 }}
                        aria-hidden="true"
                      ></i>
                    </div>

                    {isOpen ? (
                      <div style={{ padding: "6px 14px 10px" }}>
                        {cluster.skills.map(skill => {
                          const reason = reasonFor(skill)
                          return (
                            <div
                              key={skill.skill_id}
                              className="d-flex align-items-center gap-3"
                              style={{
                                padding: "8px 0 8px 30px",
                                borderBottom: `1px solid ${CARD_BORDER}`,
                              }}
                            >
                              <Input
                                type="checkbox"
                                id={`eng-sb-skill-${skill.skill_id}`}
                                checked={tickedSet.has(skill.skill_id)}
                                onChange={() => toggleSkill(skill.skill_id)}
                                style={CHECKBOX}
                                className="m-0"
                              />
                              <label
                                htmlFor={`eng-sb-skill-${skill.skill_id}`}
                                className="m-0"
                                style={{
                                  color: SKILL,
                                  fontSize: 12.5,
                                  flexGrow: 1,
                                  minWidth: 0,
                                  cursor: "pointer",
                                }}
                              >
                                {plainText(skill.skill_name || "") ||
                                  `Skill ${skill.skill_id}`}
                                {skill.sub_topic_name ? (
                                  <span style={{ color: MUTED, fontSize: 11 }}>
                                    {" "}
                                    · {plainText(skill.sub_topic_name)}
                                  </span>
                                ) : null}
                              </label>
                              {reason ? <span style={BADGE}>{reason}</span> : null}
                              {skill.manual_marking_count > 0 ? (
                                <span style={BADGE}>
                                  {skill.manual_marking_count} to mark
                                </span>
                              ) : null}
                              <span
                                style={{ color: MUTED, fontSize: 12, whiteSpace: "nowrap" }}
                              >
                                {skill.question_count} question
                                {skill.question_count === 1 ? "" : "s"}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    ) : null}
                  </div>
                )
              })}

              {noneTicked ? (
                <div className="text-danger font-size-12 mt-1">
                  Select at least one skill.
                </div>
              ) : null}

              {preview.already_assigned_skill_ids.length > 0 ? (
                <div style={{ ...HELPER, marginTop: 8 }}>
                  {preview.already_assigned_skill_ids.length} skill
                  {preview.already_assigned_skill_ids.length === 1 ? "" : "s"} already{" "}
                  {preview.already_assigned_skill_ids.length === 1 ? "has" : "have"} a Skill
                  Builder and {preview.already_assigned_skill_ids.length === 1 ? "is" : "are"} not
                  listed again.
                </div>
              ) : null}

              {/* A gap with no question written for it. Worth showing rather than hiding: only
                  the curriculum team can close it, and they need to be told which. */}
              {preview.skipped_no_questions.length > 0 ? (
                <div style={{ ...HELPER, marginTop: 8 }}>
                  {preview.skipped_no_questions.length} skill
                  {preview.skipped_no_questions.length === 1 ? " has" : "s have"} a gap but no
                  question written yet, so {preview.skipped_no_questions.length === 1 ? "it" : "they"}{" "}
                  cannot be assigned:{" "}
                  {preview.skipped_no_questions
                    .map(s => plainText(s.skill_name || "") || s.skill_id)
                    .join(", ")}
                </div>
              ) : null}
            </>
          )}

          <hr style={{ borderColor: CARD_BORDER, margin: "20px 0 16px" }} />

          <div className="d-flex align-items-center flex-wrap gap-3">
            <button
              type="button"
              className="btn fw-bold"
              style={{
                backgroundColor: canAdd ? ACCENT : "#e9ecf1",
                borderColor: canAdd ? ACCENT : "#e9ecf1",
                color: canAdd ? "#fff" : MUTED,
                borderRadius: 6,
                padding: "9px 20px",
                fontSize: 13,
              }}
              disabled={!canAdd}
              onClick={handleStage}
            >
              {alreadyStaged ? "Already added" : "Add to assignment"}
            </button>
            <span style={HELPER}>
              {nothingOwed
                ? "Available once there is a gap to remediate."
                : "Confirmed when you press Add Activities."}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  )
}

/**
 * A stable key for a section.
 *
 * `cluster_id` is null when the skill's cluster row was retired from the tree, and that section
 * still has to open and tick like any other - so null becomes 0 rather than being dropped.
 */
function sectionKey(clusterId: number | null): number {
  return clusterId == null ? 0 : clusterId
}

export default EngDiagSkillBuilderTabPanel
